/**
 * Maps API failures to what the UI shows. One place, so every screen phrases
 * the same failure the same way and no raw backend detail leaks to a user.
 */
import { ApiError, isApiError } from '../api/client';

export interface UiError {
  /** Short, human sentence. */
  message: string;
  /** Whether a "Try again" affordance makes sense. */
  retryable: boolean;
  /** Which upsell the Pro prompt should raise, if the failure is a plan/credit gate. */
  upgrade?: 'credits' | 'plan' | 'cap' | null;
  code: string;
  status: number;
}

export function toUiError(e: unknown): UiError {
  if (isApiError(e)) return fromApiError(e);
  if (e instanceof Error && e.name === 'AbortError') return { message: 'Cancelled.', retryable: false, code: 'aborted', status: 0 };
  return { message: 'Something went wrong on this page. Refresh and try again.', retryable: true, code: 'unknown', status: 0 };
}

function fromApiError(e: ApiError): UiError {
  const base = { code: e.code, status: e.status };
  switch (e.code) {
    case 'network':
      return { ...base, message: e.message, retryable: true };
    case 'unauthenticated':
      return { ...base, message: 'Your session has ended. Sign in again to continue.', retryable: false };
    case 'insufficient_credits':
      return { ...base, message: e.message || 'You are out of credits.', retryable: false, upgrade: 'credits' };
    case 'plan_required':
      return { ...base, message: e.message || 'This needs a Pro plan.', retryable: false, upgrade: 'plan' };
    case 'resume_cap_reached':
      return { ...base, message: e.message || 'You have used all your resume slots.', retryable: false, upgrade: 'cap' };
    case 'csrf':
    case 'forbidden':
      return { ...base, message: 'That action was not allowed. Refresh the page and try again.', retryable: true };
    case 'not_found':
      return { ...base, message: 'That is no longer available.', retryable: false };
    case 'job_closed':
      return { ...base, message: 'This job has expired. No credit was used.', retryable: false };
    case 'version_conflict':
      return { ...base, message: 'This resume was changed somewhere else. Reloading the latest version.', retryable: true };
    case 'conflict':
    case 'already_exists':
      return { ...base, message: e.message || 'That already exists.', retryable: false };
    case 'file_too_large':
      return { ...base, message: 'That file is too big. The limit is 10 MB.', retryable: false };
    case 'unsupported_file_type':
      return { ...base, message: 'Please upload a PDF or DOCX.', retryable: false };
    case 'unreadable_file':
      return { ...base, message: "We couldn't read that file. Try exporting it again as a PDF.", retryable: false };
    case 'rate_limited': {
      const secs = e.retryAfterSeconds;
      const when = secs ? (secs >= 3600 ? `about ${Math.round(secs / 3600)} hour${secs >= 7200 ? 's' : ''}` : secs >= 60 ? `about ${Math.round(secs / 60)} minute${secs >= 120 ? 's' : ''}` : `${secs} seconds`) : 'a moment';
      return { ...base, message: `Too many attempts. Try again in ${when}.`, retryable: false };
    }
    case 'validation_error':
      return { ...base, message: e.message || 'Please check the highlighted fields.', retryable: false };
    case 'upstream_failed':
    case 'ai_unavailable':
      return { ...base, message: 'ClimbJobs could not finish that right now. Nothing was charged. Please try again shortly.', retryable: true };
    case 'email_send_failed':
      return { ...base, message: "We couldn't send that link. Try again.", retryable: true };
    default:
      if (e.status >= 500) return { ...base, message: 'ClimbJobs is having trouble right now. Please try again in a minute.', retryable: true };
      return { ...base, message: e.message || 'Something went wrong.', retryable: e.status === 0 };
  }
}
